import React, { useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import { useTranslate } from 'react-admin';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

export interface PickerCourse {
    id: number;
    title: string;
}

export interface CoursePickerProps {
    /** Eigene Kurse des Mandanten */
    courses: PickerCourse[];
    /** Geordnete Auswahl; der erste Kurs ist der Primärkurs */
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    /** Kurse, die schon in einem anderen Angebot stecken (nicht wählbar) */
    takenIds?: number[];
    /** Auswahl nicht änderbar (veröffentlichtes Angebot) */
    disabled?: boolean;
}

/**
 * Mehrfachauswahl der eigenen Kurse für ein Angebot (Bundles): Suchfeld und
 * Checkbox-Liste in der Wizard-Optik, darunter die gewählten Kurse als Pills in
 * Reihenfolge. Der erste Kurs ist der Primärkurs; die Reihenfolge lässt sich
 * mit „früher“ ändern.
 */
const CoursePicker: React.FC<CoursePickerProps> = ({ courses, selectedIds, onChange, takenIds = [], disabled = false }) => {
    const translate = useTranslate();
    const [query, setQuery] = useState('');

    const byId = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q === '' ? courses : courses.filter((c) => c.title.toLowerCase().includes(q));
    }, [courses, query]);

    const toggle = (id: number) => {
        if (disabled) return;
        onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    };

    const moveEarlier = (index: number) => {
        if (index <= 0) return;
        const next = [...selectedIds];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        onChange(next);
    };

    return (
        <div className="mk-cpick" data-testid="course-picker">
            {selectedIds.length > 0 && (
                <ul className="mk-sel" aria-label={translate('marketplaceSeller.editor.selectedCourses')} data-testid="selected-courses">
                    {selectedIds.map((id, index) => {
                        const title = byId.get(id)?.title ?? `#${id}`;
                        return (
                            <li key={id}>
                                <span className="mk-pill line" data-testid={`selected-course-${id}`}>
                                    <span className="no">{index + 1}</span>
                                    {title}
                                    {index === 0 && selectedIds.length > 1 && (
                                        <span className="mk-pill neu" style={{ marginLeft: 4 }}>{translate('marketplaceSeller.editor.primary')}</span>
                                    )}
                                    {!disabled && index > 0 && (
                                        <button type="button" onClick={() => moveEarlier(index)} aria-label={translate('marketplaceSeller.editor.moveEarlier', { title })}>
                                            <MaterialSymbol name="arrow_back" />
                                        </button>
                                    )}
                                    {!disabled && (
                                        <button type="button" onClick={() => toggle(id)} aria-label={translate('marketplaceSeller.editor.removeCourse', { title })}>
                                            <MaterialSymbol name="close" />
                                        </button>
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
            {!disabled && (
                <>
                    <TextField
                        size="small"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={translate('marketplaceSeller.editor.searchCourses')}
                        slotProps={{ htmlInput: { 'aria-label': translate('marketplaceSeller.editor.searchCourses') } }}
                    />
                    <div className="mk-cpick-list" role="group" aria-label={translate('marketplaceSeller.editor.course')}>
                        {filtered.length === 0 && <div className="mk-cpick-empty">{translate('marketplaceSeller.editor.noCourseFound')}</div>}
                        {filtered.map((c) => {
                            const taken = takenIds.includes(c.id) && !selectedIds.includes(c.id);
                            const on = selectedIds.includes(c.id);
                            return (
                                <label key={c.id} className={`mk-cpick-row${on ? ' on' : ''}${taken ? ' off' : ''}`}>
                                    <input type="checkbox" checked={on} disabled={taken} onChange={() => toggle(c.id)} />
                                    <span className="t">{c.title}</span>
                                    {taken && <span className="s">{translate('marketplaceSeller.editor.alreadyOffered')}</span>}
                                </label>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default CoursePicker;
